// src/components/tour/tours/starlink.jsx
//
// Starlink device page tour - one component (StarlinkDevicePage.jsx)
// serves /admin/starlink/:id and the client /starlink/:id. FleetHeader's
// scope picks the usage explanation: internal admins see OUR
// subscription vs the client's plan, clients see their own plan.

import React from 'react';
import { replayStep } from './common';

const fmt = (n, d = 1) => (Number.isFinite(Number(n)) ? Number(n).toFixed(d) : '—');

export function getStarlinkSteps(ctx, env) {
    const data = ctx.data || {};
    const { scope } = env;
    const isAdmin = scope !== 'client';
    const { plan, usage, quota, live = {}, location, alerts = [] } = data;
    const hasLimit = usage?.limitGB != null;

    return [
        {
            target: null,
            title: `Starlink ${data.kit || 'terminal'}`,
            content: (
                <p>
                    Usage and live health for one Starlink terminal
                    {data.site_name && <> on <span className="pt-data">{data.site_name}</span></>}.
                    Right now it's{' '}
                    <span className={`t247-live-badge ${data.online ? '' : 'is-off'}`}>
                        <span className="t247-dot" />{data.online ? 'ONLINE' : 'OFFLINE'}
                    </span>
                </p>
            ),
        },
        {
            target: '[data-tour="sl-identity"]',
            title: 'Which terminal',
            content: (
                <p>
                    The site and kit you're looking at, and whether it's online (with uptime).
                    {' '}When {isAdmin ? 'there are spare kits on the same service line' : 'you have more than one terminal'},
                    a menu on the right switches between them.
                </p>
            ),
        },
        {
            when: () => alerts.length > 0,
            target: '[data-tour="sl-alerts"]',
            title: 'Active warnings',
            content: <p>Anything the terminal itself is currently reporting, such as obstruction or a software update.</p>,
        },
        {
            target: '[data-tour="sl-usage"]',
            title: isAdmin ? 'Subscription vs client plan' : 'Your data this cycle',
            content: isAdmin && quota ? (
                <>
                    <p>
                        <span className="pt-data">{fmt(quota.usedGB, 2)} GB</span> used. The bar compares it with what
                        <b> we subscribe</b> upstream ({quota.subscribedGB != null ? <span className="pt-data">{fmt(quota.subscribedGB, 0)} GB</span> : '—'})
                        and what the <b>client bought</b>{quota.clientGB != null && <> (<span className="pt-data">{fmt(quota.clientGB, 0)} GB</span>)</>}.
                    </p>
                    <p>
                        The dashed line is the top-up threshold; the status tells you whether to top up before
                        the client gets throttled. The client plan comes from the billing yacht linked in Site Manager.
                    </p>
                </>
            ) : (
                <>
                    <p>
                        {usage
                            ? <><span className="pt-data">{fmt(usage.usedGB, 2)} GB</span> used this billing cycle</>
                            : 'Data used this billing cycle'}
                        {hasLimit
                            ? <> out of your <span className="pt-data">{fmt(usage.limitGB, 0)} GB</span> plan ({plan?.name}).</>
                            : '. No plan limit is set, so usage is shown without a cap.'}
                    </p>
                    {hasLimit && <p>The mark on the bar is 80%: past it you're approaching your cap.</p>}
                </>
            ),
        },
        {
            target: '[data-tour="sl-forecast"]',
            title: 'Forecast',
            content: (
                <p>
                    Average per day{usage?.avgPerDayGB != null && <> (<span className="pt-data">{fmt(usage.avgPerDayGB, 2)} GB</span>)</>},
                    {' '}the forecast at the end of the cycle if usage stays the same
                    {usage?.projectedGB != null && <> (<span className="pt-data">{fmt(usage.projectedGB, 1)} GB</span>)</>},
                    {' '}and when the cycle resets.
                </p>
            ),
        },
        {
            target: '[data-tour="sl-charts"]',
            title: 'Usage history',
            content: <p>Data per day in this cycle, and per month for the last 12 months. Hover or tap a bar for the exact value.</p>,
        },
        {
            target: '[data-tour="sl-telemetry"]',
            title: 'Live telemetry',
            content: (
                <>
                    <p>
                        Current speeds, latency{live.latencyMs != null && <> (<span className="pt-data">{live.latencyMs} ms</span>)</>} and
                        ping drop, plus signal quality and obstruction (something blocking the dish's view of the sky).
                    </p>
                    <p>It refreshes every minute; the small charts build up while this page stays open.</p>
                </>
            ),
        },
        {
            target: '[data-tour="sl-terminal"]',
            title: 'Terminal & service',
            content: (
                <p>
                    Identifiers you may need for support (terminal ID, kit and dish serial, software, public IP)
                    {isAdmin ? ', our subscription and the client plan' : ' and your service plan'}.
                </p>
            ),
        },
        {
            when: () => !!location,
            target: '[data-tour="sl-location"]',
            title: 'Approximate location',
            content: <p>Roughly where the terminal is (about 10 km accuracy). Starlink doesn't share an exact GPS position.</p>,
        },
        replayStep,
    ];
}
